import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RotateCw, ShoppingBag, Unplug } from "lucide-react";
import type {
  ShopifyOrderSyncStatus,
  ShopifyPaymentMethod,
  ShopifySettingsResponseDto,
  ShopifyStoreDto,
  ShopifyStoreSettingsDto,
} from "@finora/types";
import { ApiError } from "@finora/api-client";
import { formatMoney } from "@finora/config";
import { Button, EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import { api } from "../../lib/api";

/**
 * Shopify integracija (28.09.2026) - povezivanje prodavnice (OAuth), strogo
 * mapiranje načina plaćanja, izvori PIB-a, pravila i pregled narudžbina.
 * Vidi docs/shopify-integracija.md.
 */

const PAYMENT_LABELS: Record<ShopifyPaymentMethod, string> = {
  BANK_TRANSFER: "Prenos na račun",
  CARD: "Platna kartica",
  INSTANT_PAYMENT: "IPS (instant plaćanje)",
  CASH: "Gotovina / pouzeće",
};

const STATUS: Record<ShopifyOrderSyncStatus, { label: string; tone: "success" | "warning" | "error" | "neutral" | "info" }> = {
  PENDING: { label: "Na čekanju", tone: "neutral" },
  WAITING_FULFILLMENT: { label: "Čeka isporuku (pouzeće)", tone: "info" },
  FISCALIZED: { label: "Fiskalizovano", tone: "success" },
  BLOCKED_UNMAPPED_PAYMENT: { label: "Nemapirano plaćanje", tone: "error" },
  BLOCKED_LICENSE: { label: "Dodatak Shopify nije aktivan", tone: "warning" },
  BLOCKED_VALIDATION: { label: "Greška - ručno", tone: "error" },
  FAILED_RETRYING: { label: "Ponovni pokušaj", tone: "warning" },
  FAILED_FINAL: { label: "Neuspelo", tone: "error" },
  SKIPPED: { label: "Preskočeno", tone: "neutral" },
  REFUNDED: { label: "Stornirano", tone: "info" },
  REFUNDED_PARTIAL: { label: "Delimična refundacija (staro)", tone: "warning" },
};

function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.response.message;
  return e instanceof Error ? e.message : String(e);
}

function ConnectCard({ initialShop }: { initialShop: string }) {
  const [shop, setShop] = useState(initialShop);
  const connect = useMutation({
    mutationFn: () => api.getShopifyInstallUrl(shop.trim()),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
  });
  return (
    <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
      <h3 className="text-sm font-semibold text-slate-900">Poveži Shopify prodavnicu</h3>
      <p className="mt-1 text-sm text-slate-500">
        Unesi adresu prodavnice (npr. <span className="font-mono">moja-prodavnica.myshopify.com</span>) - bićeš
        preusmeren u Shopify da odobriš aplikaciju HAFiskal.
      </p>
      <div className="mt-3 flex gap-2">
        <input
          value={shop}
          onChange={(e) => setShop(e.target.value)}
          placeholder="moja-prodavnica.myshopify.com"
          className="h-10 flex-1 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
        />
        <Button disabled={!shop.trim() || connect.isPending} onClick={() => connect.mutate()}>
          Poveži
        </Button>
      </div>
      {connect.isError && <p className="mt-2 text-sm text-red-600">{errorText(connect.error)}</p>}
    </div>
  );
}

function SettingsPanel({ store }: { store: ShopifyStoreDto }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["shopify-settings", store.id],
    queryFn: () => api.getShopifySettings(store.id),
  });
  const { data: locations } = useQuery({
    queryKey: ["business-location-options"],
    queryFn: () => api.listBusinessLocationOptions(),
  });
  const [draft, setDraft] = useState<ShopifyStoreSettingsDto | null>(null);
  const [locationId, setLocationId] = useState<string>("");
  const [savedInfo, setSavedInfo] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setDraft(data.settings);
      setLocationId(data.businessLocationId ?? "");
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.updateShopifySettings(store.id, {
        paymentMap: draft!.paymentMap,
        pibSources: draft!.pibSources,
        rules: draft!.rules,
        businessLocationId: locationId || null,
      }),
    onSuccess: (res: ShopifySettingsResponseDto) => {
      setSavedInfo(
        res.requeued ? `Sačuvano. ${res.requeued} narudžbina vraćeno u obradu.` : "Sačuvano.",
      );
      qc.invalidateQueries({ queryKey: ["shopify-settings", store.id] });
      qc.invalidateQueries({ queryKey: ["shopify-orders", store.id] });
      qc.invalidateQueries({ queryKey: ["shopify-stores"] });
    },
  });

  if (isLoading || !data || !draft) return <Skeleton className="h-40 w-full" />;

  const setMap = (key: string, value: string) =>
    setDraft({ ...draft, paymentMap: { ...draft.paymentMap, [key]: (value || null) as ShopifyPaymentMethod | null } });
  const setRule = (k: keyof ShopifyStoreSettingsDto["rules"], v: boolean | string) =>
    setDraft({ ...draft, rules: { ...draft.rules, [k]: v } });
  const setPib = (k: keyof ShopifyStoreSettingsDto["pibSources"], v: boolean | string | null) =>
    setDraft({ ...draft, pibSources: { ...draft.pibSources, [k]: v } });

  const toggle = (label: string, value: boolean, onChange: (v: boolean) => void) => (
    <label className="flex items-center justify-between gap-3 py-1 text-sm text-slate-700">
      <span>{label}</span>
      <select
        value={value ? "1" : "0"}
        onChange={(e) => onChange(e.target.value === "1")}
        className="h-8 rounded-md border border-border px-2 text-sm"
      >
        <option value="1">Da</option>
        <option value="0">Ne</option>
      </select>
    </label>
  );

  return (
    <div className="space-y-5">
      <section>
        <h4 className="text-sm font-semibold text-slate-900">Mapiranje načina plaćanja (obavezno)</h4>
        <p className="mt-1 text-xs text-slate-500">
          Svaki Shopify način plaćanja mora biti eksplicitno mapiran. Nemapiran = narudžbina se NE fiskalizuje
          dok ga ne mapiraš. Predlog nije mapiranje dok ne klikneš Sačuvaj.
        </p>
        {data.gateways.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">
            Još nema viđenih načina plaćanja - pojaviće se posle prve plaćene narudžbine.
          </p>
        ) : (
          <table className="mt-2">
            <thead>
              <tr>
                <th>Shopify gateway</th>
                <th>HAFiskal način plaćanja</th>
                <th>Predlog</th>
              </tr>
            </thead>
            <tbody>
              {data.gateways.map((g) => (
                <tr key={g.key}>
                  <td>
                    <div className="text-slate-800">{g.name}</div>
                    <div className="font-mono text-xs text-slate-400">{g.key}</div>
                  </td>
                  <td>
                    <select
                      value={draft.paymentMap[g.key] ?? ""}
                      onChange={(e) => setMap(g.key, e.target.value)}
                      className="h-9 rounded-md border border-border px-2 text-sm"
                    >
                      <option value="">— nije mapirano —</option>
                      {(Object.keys(PAYMENT_LABELS) as ShopifyPaymentMethod[]).map((k) => (
                        <option key={k} value={k}>
                          {PAYMENT_LABELS[k]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="text-xs text-slate-500">
                    {g.suggestion ? (
                      <button
                        type="button"
                        className="text-brand hover:underline"
                        onClick={() => setMap(g.key, g.suggestion!)}
                      >
                        {PAYMENT_LABELS[g.suggestion]} (primeni)
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="grid gap-5 md:grid-cols-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">PIB kupca (pravno lice) - redosled izvora</h4>
          {toggle(
            "0. HAFiskal polja: Fizičko/Pravno lice, firma, PIB, MB (korpa, checkout; rezerva: polja kupca)",
            draft.pibSources.hafiscalFields,
            (v) => setPib("hafiscalFields", v),
          )}
          <p className="pb-1 text-xs text-slate-500">
            Ključevi hafiscal_tip_kupca / hafiscal_firma / hafiscal_pib / hafiscal_mb i metafield-i kupca hafiscal.*.
            &quot;Fizičko lice&quot; = račun uvek bez kupca.
          </p>
          {toggle("1. B2B firma (poreski broj lokacije)", draft.pibSources.b2bCompany, (v) => setPib("b2bCompany", v))}
          <label className="block py-1 text-sm text-slate-700">
            2. Atribut narudžbine (ključ)
            <input
              value={draft.pibSources.attributeKey ?? ""}
              onChange={(e) => setPib("attributeKey", e.target.value || null)}
              placeholder="PIB (prazno = isključeno)"
              className="mt-1 h-9 w-full rounded-md border border-border px-2 text-sm"
            />
          </label>
          <label className="block py-1 text-sm text-slate-700">
            Atribut sa nazivom firme
            <input
              value={draft.pibSources.companyAttributeKey ?? ""}
              onChange={(e) => setPib("companyAttributeKey", e.target.value || null)}
              placeholder="Firma"
              className="mt-1 h-9 w-full rounded-md border border-border px-2 text-sm"
            />
          </label>
          <label className="block py-1 text-sm text-slate-700">
            3. Metafield kupca (namespace.key)
            <input
              value={draft.pibSources.customerMetafield ?? ""}
              onChange={(e) => setPib("customerMetafield", e.target.value || null)}
              placeholder="finora.pib (prazno = isključeno)"
              className="mt-1 h-9 w-full rounded-md border border-border px-2 font-mono text-sm"
            />
          </label>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Pravila</h4>
          {toggle("Pravno lice - fiskalni račun", draft.rules.legalEntityFiscalReceipt, (v) => setRule("legalEntityFiscalReceipt", v))}
          {toggle("Fizičko lice - fiskalni račun", draft.rules.physicalPersonFiscalReceipt, (v) => setRule("physicalPersonFiscalReceipt", v))}
          {toggle("Pouzeće fiskalizuj tek kad je isporučeno", draft.rules.codOnFulfilled, (v) => setRule("codOnFulfilled", v))}
          {toggle("Pošalji fiskalni račun kupcu mejlom", draft.rules.sendReceiptEmail, (v) => setRule("sendReceiptEmail", v))}
          {toggle("Upiši firmu, PIB i MB u polja kupca u Shopify adminu", draft.rules.saveCustomerFields, (v) => setRule("saveCustomerFields", v))}
          {toggle("Preskoči test narudžbine", draft.rules.skipTestOrders, (v) => setRule("skipTestOrders", v))}
          <label className="flex items-center justify-between gap-3 py-1 text-sm text-slate-700">
            <span>PDV stopa za stavke bez Shopify poreza (%)</span>
            <input
              value={draft.rules.defaultTaxRate}
              onChange={(e) => setRule("defaultTaxRate", e.target.value)}
              className="h-8 w-20 rounded-md border border-border px-2 text-sm"
            />
          </label>
          <label className="block py-1 text-sm text-slate-700">
            Poslovna lokacija
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="mt-1 h-9 w-full rounded-md border border-border px-2 text-sm"
            >
              <option value="">Podrazumevana lokacija firme</option>
              {locations?.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </label>
          <p className="mt-2 text-xs text-slate-400">eFaktura za Shopify (pravna lica) nije deo ove verzije.</p>
        </div>
      </section>

      <div className="flex items-center gap-3">
        <Button disabled={save.isPending} onClick={() => save.mutate()}>
          Sačuvaj podešavanja
        </Button>
        {save.isError && <span className="text-sm text-red-600">{errorText(save.error)}</span>}
        {savedInfo && !save.isError && <span className="text-sm text-emerald-700">{savedInfo}</span>}
      </div>
    </div>
  );
}

const REFUND_STATUS: Record<string, string> = {
  PENDING: "Na čekanju",
  WAITING_ORIGINAL: "Čeka račun / prethodnu refundaciju",
  DONE: "Urađeno",
  NEEDS_MANUAL: "Ručna provera",
  FAILED_RETRYING: "Ponovni pokušaj",
  SKIPPED: "Preskočeno",
};

function OrdersPanel({ store }: { store: ShopifyStoreDto }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["shopify-orders", store.id, status],
    queryFn: () => api.listShopifyOrders(store.id, { status: status || undefined, limit: 100 }),
    refetchInterval: 30_000,
  });
  const retry = useMutation({
    mutationFn: (syncId: string) => api.retryShopifyOrder(store.id, syncId),
    onSettled: () => qc.invalidateQueries({ queryKey: ["shopify-orders", store.id] }),
  });
  const retryRefund = useMutation({
    mutationFn: (id: string) => api.retryShopifyRefund(store.id, id),
    onSettled: () => qc.invalidateQueries({ queryKey: ["shopify-orders", store.id] }),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-semibold text-slate-900">Narudžbine</h4>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 rounded-md border border-border px-2 text-sm">
          <option value="">Sve</option>
          {(Object.keys(STATUS) as ShopifyOrderSyncStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS[s].label}
            </option>
          ))}
        </select>
      </div>
      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : !data || data.items.length === 0 ? (
        <p className="text-sm text-slate-500">Nema narudžbina.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Narudžbina</th>
              <th>Iznos</th>
              <th>Status</th>
              <th>Poruka</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.items.map((o) => (
              <tr key={o.id}>
                <td>
                  <div className="text-slate-800">{o.orderName ?? o.shopifyOrderId}</div>
                  <div className="text-xs text-slate-400">{new Date(o.createdAt).toLocaleString("sr-RS")}</div>
                </td>
                <td className="text-slate-700">{o.totalAmount ? formatMoney(o.totalAmount) : "—"}</td>
                <td>
                  <StatusBadge label={STATUS[o.status]?.label ?? o.status} tone={STATUS[o.status]?.tone ?? "neutral"} />
                  {o.writebackStatus === "FAILED" && (
                    <div className="mt-1 text-xs text-amber-600">Upis u Shopify nije uspeo (ponavlja se)</div>
                  )}
                </td>
                <td className="max-w-md text-xs text-slate-500">
                  {o.lastError ?? (o.buyerPib ? `PIB ${o.buyerPib}` : "")}
                  {o.reissueCount > 0 && (
                    <div className="mt-1 text-slate-600">
                      Posle refundacija izdat nov račun ({o.reissueCount}x); stornirani:{" "}
                      {o.fiscalHistory.map((h) => h.fiscalNumber ?? h.documentId).join(", ")}
                    </div>
                  )}
                </td>
                <td>
                  {o.status !== "FISCALIZED" && o.status !== "REFUNDED" && (
                    <Button variant="secondary" disabled={retry.isPending} onClick={() => retry.mutate(o.id)}>
                      <RotateCw className="mr-1 h-4 w-4" aria-hidden /> Fiskalizuj ponovo
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data && data.refunds.length > 0 && (
        <>
          <h4 className="pt-2 text-sm font-semibold text-slate-900">Refundacije (storno + nov račun za zadržano)</h4>
          <table>
            <thead>
              <tr>
                <th>Refundacija</th>
                <th>Refundirano</th>
                <th>Nov račun</th>
                <th>Status</th>
                <th>Poruka</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.refunds.map((r) => (
                <tr key={r.id}>
                  <td className="font-mono text-xs">{r.shopifyRefundId}</td>
                  <td>{r.amount ? formatMoney(r.amount) : "—"}</td>
                  <td>{r.remainingAmount == null ? "—" : Number(r.remainingAmount) > 0 ? formatMoney(r.remainingAmount) : "samo storno"}</td>
                  <td>{REFUND_STATUS[r.status] ?? r.status}</td>
                  <td className="max-w-md text-xs text-slate-500">{r.lastError ?? ""}</td>
                  <td>
                    {r.status !== "DONE" && r.status !== "SKIPPED" && (
                      <Button variant="secondary" disabled={retryRefund.isPending} onClick={() => retryRefund.mutate(r.id)}>
                        Ponovi
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

export function ShopifyTab() {
  const qc = useQueryClient();
  const params = new URLSearchParams(window.location.search);
  const { data: stores, isLoading } = useQuery({ queryKey: ["shopify-stores"], queryFn: () => api.listShopifyStores() });
  const disconnect = useMutation({
    mutationFn: (id: string) => api.disconnectShopifyStore(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shopify-stores"] }),
  });
  const reRegister = useMutation({
    mutationFn: (id: string) => api.registerShopifyWebhooks(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shopify-stores"] }),
  });

  return (
    <div className="space-y-4">
      {params.get("connected") && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Prodavnica {params.get("connected")} je povezana. Mapiraj načine plaćanja ispod.
        </p>
      )}
      {params.get("warning") && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{params.get("warning")}</p>
      )}
      {params.get("error") && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{params.get("error")}</p>}

      <ConnectCard initialShop={params.get("shop") ?? ""} />

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : !stores || stores.length === 0 ? (
        <EmptyState icon={ShoppingBag} title="Nema povezanih prodavnica" description="Poveži Shopify prodavnicu iznad." />
      ) : (
        stores.map((s) => (
          <div key={s.id} className="space-y-5 rounded-xl border border-border bg-surface p-5 shadow-subtle">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">{s.shopName ?? s.shopDomain}</h3>
                <div className="font-mono text-xs text-slate-500">{s.shopDomain}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <StatusBadge label={s.connected ? "Povezano" : "Nije povezano"} tone={s.connected ? "success" : "error"} />
                  <StatusBadge label={s.licenseActive ? "Licenca aktivna" : "Dodatak Shopify nije aktivan"} tone={s.licenseActive ? "success" : "warning"} />
                  <StatusBadge label={s.webhooksRegisteredAt ? "Webhookovi registrovani" : "Webhookovi NISU registrovani"} tone={s.webhooksRegisteredAt ? "success" : "error"} />
                  <StatusBadge
                    label={s.customerFieldsScope ? "Polja kupca: dozvola odobrena" : "Polja kupca: potrebno ponovno odobrenje (Poveži)"}
                    tone={s.customerFieldsScope ? "success" : "warning"}
                  />
                </div>
                {s.lastError && <p className="mt-2 text-xs text-red-600">{s.lastError}</p>}
                {!s.licenseActive && s.licenseMessage && <p className="mt-2 text-xs text-amber-700">{s.licenseMessage}</p>}
              </div>
              <div className="flex gap-2">
                {s.connected && (
                  <Button variant="secondary" disabled={reRegister.isPending} onClick={() => reRegister.mutate(s.id)}>
                    <RotateCw className="mr-1 h-4 w-4" aria-hidden /> Webhookovi i polja kupca
                  </Button>
                )}
                {s.connected && (
                  <Button
                    variant="secondary"
                    disabled={disconnect.isPending}
                    onClick={() => {
                      if (window.confirm("Prekinuti vezu sa ovom Shopify prodavnicom?")) disconnect.mutate(s.id);
                    }}
                  >
                    <Unplug className="mr-1 h-4 w-4" aria-hidden /> Prekini vezu
                  </Button>
                )}
              </div>
            </div>
            <SettingsPanel store={s} />
            <OrdersPanel store={s} />
          </div>
        ))
      )}
    </div>
  );
}
